/* eslint-disable no-use-before-define */
import { Box } from '@mui/material';
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
const containerStyle = {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
};
export default function VisibleAreaProbe({ onVisibleAreaChange, ...rest }) {
    const callbackRef = useRef(onVisibleAreaChange);
    callbackRef.current = onVisibleAreaChange;
    const containerRef = useRef(null);
    const containerRectRef = useRef();
    const windowSizeRef = useRef([0, 0]);
    const animationRef = useRef();
    const dataRef = useRef();
    const onChange = useCallback((data) => {
        const oldData = dataRef.current;
        if (!oldData ||
            data.visible !== oldData.visible ||
            (data.visible &&
                (data.left !== oldData.left ||
                    data.top !== oldData.top ||
                    data.width !== oldData.width ||
                    data.height !== oldData.height))) {
            callbackRef.current?.(data);
            dataRef.current = data;
        }
    }, []);
    const bodyObserver = useMemo(() => {
        const observer = new IntersectionObserver(([entry]) => {
            const container = containerRef.current;
            const { target, boundingClientRect, intersectionRect } = entry;
            observer.unobserve(target);
            const visible = intersectionRect.width > 0 && intersectionRect.height > 0;
            const left = intersectionRect.left - boundingClientRect.left;
            const top = intersectionRect.top - boundingClientRect.top;
            const { width, height } = intersectionRect;
            onChange({
                left,
                top,
                width,
                height,
                visible,
            });
            container?.removeChild(target);
            animationRef.current = requestAnimationFrame(update);
        });
        return observer;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const update = useCallback(() => {
        const container = containerRef.current;
        const rect = container?.getBoundingClientRect();
        const oldRect = containerRectRef.current;
        const oldWindowSize = windowSizeRef.current;
        if (rect &&
            oldRect &&
            oldWindowSize[0] === window.innerWidth &&
            oldWindowSize[1] === window.innerHeight &&
            rect.left === oldRect.left &&
            rect.top === oldRect.top &&
            rect.right === oldRect.right &&
            rect.bottom === oldRect.bottom &&
            rect.width === oldRect.width &&
            rect.height === oldRect.height) {
            // Rect unchanged
            animationRef.current = requestAnimationFrame(update);
        }
        else {
            // We place a "probe" DOM into the container, so it will call the bodyObserver callback immediately
            // And once the callback is called, remove the probe and wait until next frame
            const probe = document.createElement('div');
            const { style } = probe;
            style.position = 'absolute';
            style.left = '0';
            style.right = '0';
            style.top = '0';
            style.bottom = '0';
            style.pointerEvents = 'none';
            container?.appendChild(probe);
            bodyObserver.observe(probe);
        }
        containerRectRef.current = rect;
        windowSizeRef.current = [window.innerWidth, window.innerHeight];
    }, [bodyObserver]);
    useLayoutEffect(() => {
        update();
        return () => {
            // istanbul ignore next
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = undefined;
            }
        };
    }, [update]);
    return (React.createElement(Box, { ...rest, ref: containerRef, sx: [containerStyle, rest.sx] }));
}
